import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Users,
  DollarSign,
  Link2,
  Copy,
  Plus,
  RefreshCw,
  Share2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  UserPlus,
  CheckCircle2,
  Lock,
  CreditCard,
  Wallet,
  AlertCircle,
  Gift,
  HelpCircle,
  FileText,
  Building2,
  ShieldCheck,
  Clock,
  ArrowRight,
  Sparkles,
  X,
  Twitter,
  Linkedin
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AffiliateShareWidget } from "./AffiliateBanner";

const COMMISSION_RATE = 20; // Fixed 20% commission

// Stripe Connect Setup Guide Component
const StripeSetupGuide = ({ onConnect, connecting }) => {
  const [showGuide, setShowGuide] = useState(true);
  
  if (!showGuide) return null;
  
  return (
    <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-indigo-500/30 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <Wallet className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <CardTitle className="text-indigo-500 text-xl">Get Paid Automatically</CardTitle>
              <CardDescription className="text-indigo-400">
                Connect your Stripe account in under 5 minutes
              </CardDescription>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-slate-400 hover:text-slate-400"
            onClick={() => setShowGuide(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="relative space-y-6">
        {/* Steps */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">
            How to Connect Your Stripe Account
          </h3>
          
          <div className="grid gap-4">
            {/* Step 1 */}
            <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-indigo-500 mb-1">Click "Connect Stripe" Below</h4>
                <p className="text-sm text-indigo-400">
                  You'll be redirected to Stripe's secure onboarding page. We never see your banking details.
                </p>
              </div>
              <CreditCard className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
            </div>

            {/* Step 2 */}
            <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-indigo-500 mb-1">Enter Your Information</h4>
                <p className="text-sm text-indigo-400">
                  Stripe will ask for:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-indigo-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-green-400" />
                    Your legal name and date of birth
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-green-400" />
                    Last 4 digits of your SSN (US) or equivalent
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-green-400" />
                    Your address for tax purposes
                  </li>
                </ul>
              </div>
              <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
            </div>

            {/* Step 3 */}
            <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-indigo-500 mb-1">Add Your Bank Account</h4>
                <p className="text-sm text-indigo-400">
                  Link your bank account or debit card where you want to receive your commission payouts.
                </p>
              </div>
              <Building2 className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
            </div>

            {/* Step 4 */}
            <div className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-indigo-500 font-bold text-sm">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-indigo-500 mb-1">You're All Set!</h4>
                <p className="text-sm text-indigo-400">
                  Commissions will automatically transfer to your bank account when your referrals pay.
                  Payouts typically arrive in 2-3 business days.
                </p>
              </div>
              <Sparkles className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
            </div>
          </div>
        </div>

        {/* Info boxes */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-500/10 rounded-xl border border-slate-400/20">
            <div className="flex items-center gap-2 text-indigo-400 mb-2">
              <Clock className="w-4 h-4" />
              <span className="font-medium text-sm">Time Required</span>
            </div>
            <p className="text-sm text-indigo-400">
              Most people complete setup in <strong className="text-indigo-500">3-5 minutes</strong>
            </p>
          </div>
          
          <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <ShieldCheck className="w-4 h-4" />
              <span className="font-medium text-sm">Secure & Private</span>
            </div>
            <p className="text-sm text-indigo-400">
              Stripe handles all payments. We never access your banking info.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Common Questions
          </h3>
          <Accordion type="single" collapsible className="space-y-2">
            <AccordionItem value="item-1" className="border border-slate-700 rounded-lg bg-slate-800/30 px-4">
              <AccordionTrigger className="text-indigo-500 text-sm hover:no-underline py-3">
                Do I need a Stripe account already?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-400 text-sm pb-3">
                No! If you don't have a Stripe account, one will be created for you during the setup process. 
                If you do have one, you can connect your existing account.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border border-slate-700 rounded-lg bg-slate-800/30 px-4">
              <AccordionTrigger className="text-indigo-500 text-sm hover:no-underline py-3">
                When do I get paid?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-400 text-sm pb-3">
                You earn 20% commission when a referred user makes their first payment (after their free month ends). 
                The commission is automatically transferred to your bank account, typically within 2-3 business days.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border border-slate-700 rounded-lg bg-slate-800/30 px-4">
              <AccordionTrigger className="text-indigo-500 text-sm hover:no-underline py-3">
                Is there a minimum payout amount?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-400 text-sm pb-3">
                No minimum! Every commission is transferred to you automatically, no matter the amount. 
                Stripe may have their own minimum transfer thresholds (usually $1).
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border border-slate-700 rounded-lg bg-slate-800/30 px-4">
              <AccordionTrigger className="text-indigo-500 text-sm hover:no-underline py-3">
                What countries are supported?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-400 text-sm pb-3">
                Stripe Connect supports affiliates in 40+ countries including the US, Canada, UK, most of Europe, 
                Australia, and many more. You'll see if your country is supported during the onboarding process.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="border border-slate-700 rounded-lg bg-slate-800/30 px-4">
              <AccordionTrigger className="text-indigo-500 text-sm hover:no-underline py-3">
                What if I don't connect Stripe?
              </AccordionTrigger>
              <AccordionContent className="text-indigo-400 text-sm pb-3">
                Your commissions will still be tracked and accumulated. You can connect Stripe at any time to 
                receive your pending payouts. We recommend connecting sooner so you don't forget!
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* CTA Button */}
        <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-slate-700">
          <Button 
            size="lg"
            onClick={onConnect}
            disabled={connecting}
            className="w-full sm:w-auto bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25"
          >
            {connecting ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            Connect Stripe Account
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-slate-400 text-center sm:text-left">
            You'll be redirected to Stripe's secure site to complete setup
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <Card className="bg-slate-800/50 border-slate-700/50">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-indigo-400">{label}</p>
          <p className="text-2xl font-bold text-indigo-500">{value}</p>
          {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [referrals, setReferrals] = useState({});
  const [creating, setCreating] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  
  // New code form - only name now, commission is fixed
  const [newCodeName, setNewCodeName] = useState("");

  useEffect(() => {
    fetchAffiliateData();
    checkStripeStatus();
    
    // Check URL params for Stripe return
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe") === "complete") {
      toast.success("Stripe account connected successfully!");
      checkStripeStatus();
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("stripe") === "refresh") {
      toast.info("Please complete Stripe onboarding to receive payouts");
    }
  }, []);

  const fetchAffiliateData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/affiliate/codes");
      setData(res.data);
      setHasAccess(true);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.code === "FEATURE_GATED") {
        setHasAccess(false);
      } else {
        toast.error("Failed to load affiliate data");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkStripeStatus = async () => {
    try {
      const res = await api.get("/affiliate/stripe/status");
      setStripeStatus(res.data);
    } catch (e) {
      // Not connected yet
      setStripeStatus({ connected: false, onboarding_complete: false });
    }
  };

  const createAffiliateCode = async () => {
    if (!newCodeName.trim()) {
      toast.error("Please enter an affiliate name");
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post("/affiliate/codes", { affiliate_name: newCodeName });
      toast.success(`Affiliate code created: ${res.data.code}`);
      setShowCreateDialog(false);
      setNewCodeName("");
      fetchAffiliateData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create affiliate code");
    } finally {
      setCreating(false);
    }
  };

  const connectStripe = async () => {
    setConnectingStripe(true);
    try {
      const res = await api.post("/affiliate/stripe/connect", {
        origin_url: window.location.origin
      });
      
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect Stripe");
      setConnectingStripe(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await api.post("/affiliate/stripe/dashboard");
      if (res.data.url) {
        window.open(res.data.url, "_blank");
      }
    } catch (e) {
      toast.error("Failed to open Stripe dashboard");
    }
  };

  const fetchReferrals = async (affiliateId) => {
    if (referrals[affiliateId]) {
      setExpandedCode(expandedCode === affiliateId ? null : affiliateId);
      return;
    }
    
    try {
      const res = await api.get(`/affiliate/codes/${affiliateId}/referrals`);
      setReferrals(prev => ({ ...prev, [affiliateId]: res.data.referrals }));
      setExpandedCode(affiliateId);
    } catch (e) {
      toast.error("Failed to load referrals");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getShareLink = (affiliate) => {
    // Use referral_slug if available, otherwise fall back to code
    const ref = affiliate.referral_slug || affiliate.code;
    return `${window.location.origin}?ref=${ref}`;
  };

  const getReferralSlug = (affiliate) => {
    return affiliate.referral_slug || affiliate.code.toLowerCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="affiliate-loading">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="affiliate-gated">
        <div className="flex items-center gap-3">
          <Share2 className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Affiliate Program</h1>
            <p className="text-indigo-400">Earn {COMMISSION_RATE}% commission on referrals</p>
          </div>
        </div>
        
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-indigo-500 mb-2">Growth Tier Feature</h2>
            <p className="text-indigo-400 mb-6 max-w-md mx-auto">
              The Affiliate Program is available on the Growth tier. Upgrade to start earning 
              {COMMISSION_RATE}% commission by referring freelancers to ClientKit.
            </p>
            <Button 
              onClick={() => window.location.href = "/billing"}
              className="bg-gradient-to-r from-indigo-500 to-indigo-500 hover:from-indigo-600 hover:to-indigo-600"
            >
              Upgrade to Growth
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Share2 className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Affiliate Program</h1>
            <p className="text-indigo-400">Earn {COMMISSION_RATE}% commission on referrals</p>
          </div>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-500 hover:bg-slate-600" data-testid="create-affiliate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Affiliate Code
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-indigo-500">Create Affiliate Code</DialogTitle>
              <DialogDescription className="text-indigo-400">
                Create a unique code to share with potential referrals.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Affiliate Name / Label</Label>
                <Input
                  placeholder="e.g., Twitter, YouTube, Partner Name"
                  value={newCodeName}
                  onChange={(e) => setNewCodeName(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-indigo-500"
                  data-testid="affiliate-name-input"
                />
                <p className="text-xs text-slate-400">Label to help you track where referrals come from</p>
              </div>
              
              {/* Commission info - fixed rate */}
              <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-2 text-slate-400">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span className="font-medium">{COMMISSION_RATE}% Commission</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  You earn {COMMISSION_RATE}% of their first payment (after free month ends)
                </p>
              </div>
              
              {/* Referral benefit */}
              <div className="p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/30">
                <div className="flex items-center gap-2 text-slate-400">
                  <Gift className="w-4 h-4" />
                  <span className="font-medium">Referrals Get 1 Month Free</span>
                </div>
                <p className="text-xs text-indigo-400 mt-1">
                  Anyone who signs up with your code gets 1 month of Growth tier free
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600 text-slate-400">
                Cancel
              </Button>
              <Button 
                onClick={createAffiliateCode} 
                disabled={creating}
                className="bg-indigo-500 hover:bg-slate-600"
                data-testid="confirm-create-affiliate"
              >
                {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stripe Setup Guide - shown when Stripe not connected */}
      {data?.codes?.length > 0 && !stripeStatus?.onboarding_complete && (
        <StripeSetupGuide onConnect={connectStripe} connecting={connectingStripe} />
      )}

      {/* Stripe Connected Success */}
      {stripeStatus?.onboarding_complete && (
        <Alert className="bg-green-500/10 border-green-500/30">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          <AlertDescription className="text-green-200 flex items-center justify-between">
            <span>
              <strong>Stripe Connected!</strong> Commissions will be automatically transferred to your account.
            </span>
            <Button 
              size="sm" 
              variant="outline"
              onClick={openStripeDashboard}
              className="ml-4 border-green-500/50 text-green-300 hover:bg-green-500/20"
            >
              <Wallet className="w-4 h-4 mr-2" />
              View Stripe Dashboard
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Overview */}
      {data?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            icon={Link2}
            label="Active Codes"
            value={data.summary.total_codes}
            color="bg-indigo-500/20 text-indigo-400"
          />
          <StatCard
            icon={ExternalLink}
            label="Link Clicks"
            value={data.summary.total_clicks || 0}
            color="bg-indigo-500/20 text-indigo-400"
          />
          <StatCard
            icon={UserPlus}
            label="Signups"
            value={data.summary.total_referrals}
            subtext={`${data.summary.click_to_signup_rate || 0}% click→signup`}
            color="bg-blue-500/20 text-indigo-400"
          />
          <StatCard
            icon={CheckCircle2}
            label="Paid Conversions"
            value={data.summary.total_conversions}
            subtext={`${data.summary.signup_to_paid_rate || 0}% signup→paid`}
            color="bg-green-500/20 text-green-400"
          />
          <StatCard
            icon={DollarSign}
            label="Total Earnings"
            value={`$${data.summary.total_earnings.toFixed(2)}`}
            subtext={data.summary.pending_payout > 0 ? `$${data.summary.pending_payout.toFixed(2)} pending` : null}
            color="bg-indigo-500/20 text-indigo-400"
          />
        </div>
      )}

      {/* Affiliate Codes List */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-indigo-400" />
            Your Affiliate Codes
          </CardTitle>
          <CardDescription className="text-indigo-400">
            Share these codes to earn {COMMISSION_RATE}% commission
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data?.codes?.length === 0 ? (
            <div className="text-center py-8">
              <Share2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-indigo-400">No affiliate codes yet</p>
              <p className="text-sm text-slate-400">Create your first code to start earning</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data?.codes?.map((affiliate) => (
                <div key={affiliate.id} className="border border-slate-700 rounded-lg overflow-hidden">
                  {/* Code Header */}
                  <div 
                    className="p-4 bg-slate-900/50 flex items-center justify-between cursor-pointer hover:bg-slate-900/70 transition-colors"
                    onClick={() => fetchReferrals(affiliate.id)}
                    data-testid={`affiliate-code-${affiliate.code}`}
                  >
                    <div className="flex items-center gap-4">
                      {expandedCode === affiliate.id ? 
                        <ChevronDown className="w-5 h-5 text-indigo-400" /> : 
                        <ChevronRight className="w-5 h-5 text-indigo-400" />
                      }
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-lg font-mono text-indigo-400">{affiliate.code}</code>
                          <Badge className="bg-slate-400 text-slate-400">{affiliate.affiliate_name}</Badge>
                          {affiliate.click_count > 0 && (
                            <Badge className="bg-indigo-500/20 text-indigo-400">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              {affiliate.click_count} clicks
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                            ?ref={getReferralSlug(affiliate)}
                          </code>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-indigo-400">{affiliate.total_referrals} signups • {affiliate.total_conversions || 0} paid</p>
                        <p className="text-green-400 font-semibold">${(affiliate.total_earnings || 0).toFixed(2)}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-slate-600 text-slate-400"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(affiliate.code);
                          }}
                          title="Copy code"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-indigo-500/50 text-slate-400 hover:bg-slate-400/20"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(getShareLink(affiliate));
                          }}
                          title="Copy referral link"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Referrals Dropdown */}
                  {expandedCode === affiliate.id && (
                    <div className="p-4 border-t border-slate-700 bg-slate-800/30">
                      {/* Referral Link Banner */}
                      <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                        <p className="text-xs text-slate-400 font-medium mb-2">Share this link to earn commissions:</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm text-slate-600 bg-slate-900/50 px-3 py-2 rounded truncate">
                            {getShareLink(affiliate)}
                          </code>
                          <Button 
                            size="sm"
                            className="bg-indigo-500 hover:bg-slate-600"
                            onClick={() => copyToClipboard(getShareLink(affiliate))}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-medium text-slate-400 mb-3">Referral History</h4>
                      {referrals[affiliate.id]?.length === 0 ? (
                        <p className="text-slate-400 text-sm">No referrals yet for this code</p>
                      ) : (
                        <div className="space-y-2">
                          {referrals[affiliate.id]?.map((ref) => (
                            <div key={ref.id} className="flex items-center justify-between p-2 bg-slate-900/30 rounded-lg text-sm">
                              <div className="flex items-center gap-3">
                                <Badge 
                                  className={
                                    ref.status === "paid_out" ? "bg-green-500/20 text-green-400" :
                                    ref.status === "converted" ? "bg-blue-500/20 text-indigo-400" :
                                    "bg-indigo-500/20 text-indigo-400"
                                  }
                                >
                                  {ref.status === "free_month" ? "Free Month" : ref.status === "paid_out" ? "Paid Out" : ref.status}
                                </Badge>
                                <span className="text-indigo-400">{ref.source}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-slate-400">{new Date(ref.created_at).toLocaleDateString()}</p>
                                {ref.commission_earned > 0 && (
                                  <p className={ref.commission_paid ? "text-green-400" : "text-indigo-400"}>
                                    {ref.commission_paid ? "Paid" : "Pending"}: ${ref.commission_earned.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Share Widget */}
                      <div className="mt-6 pt-4 border-t border-slate-700">
                        <h4 className="text-sm font-medium text-slate-400 mb-4">Share & Promote</h4>
                        <AffiliateShareWidget referralLink={getShareLink(affiliate)} userName={affiliate.affiliate_name} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="bg-gradient-to-r from-indigo-500/10 to-indigo-500/10 border-indigo-500/30">
        <CardHeader>
          <CardTitle className="text-indigo-500">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Link2 className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="font-medium text-indigo-500 mb-1">1. Share Your Code</h3>
              <p className="text-sm text-indigo-400">Share your unique affiliate code with freelancers</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="font-medium text-indigo-500 mb-1">2. They Get Free Month</h3>
              <p className="text-sm text-indigo-400">They sign up and get 1 month of Growth tier free</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="font-medium text-indigo-500 mb-1">3. They Subscribe</h3>
              <p className="text-sm text-indigo-400">After free month, they make their first payment</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-medium text-indigo-500 mb-1">4. You Earn {COMMISSION_RATE}%</h3>
              <p className="text-sm text-indigo-400">Commission auto-transfers to your Stripe account</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
